import { intlLocale } from "@/lib/i18n"

/**
 * Formatting for the two things a `Schedule` is made of: weekdays and
 * minutes-from-midnight.
 *
 * Both anchor on a fixed UTC date and format in UTC, which is what keeps them
 * honest: `startsAt: 540` is the wall-clock 09:00 the restaurant keeps, not an
 * instant, so running it through the reader's zone would slide it by their
 * offset. The zone a schedule belongs to is already applied on the server —
 * see `Staff::LocationJourneyQuery`.
 *
 * Extracted from `business-hours-form.tsx` when the till screen needed the same
 * two functions. A second copy would have been a second definition of what
 * "09:00" means.
 */

/**
 * Monday-first, which is how a restaurant week is read and written. JavaScript's
 * `Date#getDay` is Sunday-first, so this order is stated rather than derived —
 * and the same array is declared on the server as
 * `Staff::Location::Manage::LocationOverviewProps::WEEK_ORDER`.
 */
export const WEEK = [1, 2, 3, 4, 5, 6, 0]

/** 2000-01-02 was a Sunday, so adding dayOfWeek lands on the matching weekday. */
export function weekdayName(dayOfWeek: number) {
  const date = new Date(Date.UTC(2000, 0, 2 + dayOfWeek))
  return new Intl.DateTimeFormat(intlLocale(), { weekday: "long", timeZone: "UTC" }).format(date)
}

/** For summaries, where "Lun – Jue" has to fit on one line beside its hours. */
export function weekdayShortName(dayOfWeek: number) {
  const date = new Date(Date.UTC(2000, 0, 2 + dayOfWeek))
  return new Intl.DateTimeFormat(intlLocale(), { weekday: "short", timeZone: "UTC" }).format(date)
}

export function formatMinutes(minutes: number) {
  const date = new Date(Date.UTC(2000, 0, 2, Math.floor(minutes / 60), minutes % 60))
  return new Intl.DateTimeFormat(intlLocale(), {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
  }).format(date)
}
